import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { ScrollDetail } from '@ionic/core';

/**
 * Drives the TopBar's collapsing page-title band.
 *
 * Scrolling DOWN hides the band (the user is reading, so the space is better
 * spent on content); scrolling UP brings it straight back, without having to
 * return to the top of the page. Reaching up is the gesture people make when
 * they want orientation, so it's the one that should restore the page name.
 *
 * Wiring, per page:
 *   const { collapsed, onScroll } = useCollapsingTitle(contentRef);
 *   <TopBar title={...} titleCollapsed={collapsed} />
 *   <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
 *
 * Same shape as HomePage's publish-peek scroll handler, so `scrollEvents` is
 * already a proven cost on these pages.
 */

/** Movement (px) needed before direction counts. Below this a jittery finger,
 *  a momentum tail or a layout nudge would flap the band open and shut. Deltas
 *  under the deadzone accumulate rather than being dropped, so a slow drag
 *  still registers once it adds up to a real direction. */
const DIRECTION_DEADZONE = 8;

/** Never collapse inside this top zone. Without it, the band could hide while
 *  the content under it has barely moved — and the collapse would itself shift
 *  content up, which reads as a jump. Roughly the band's own height. */
const TOP_ZONE = 48;

/** Freeze the band inside this bottom zone. The band lives in IonHeader, so
 *  showing or hiding it resizes IonContent and moves `maxScrollTop` by the
 *  band's height in the opposite direction. At the bottom `scrollTop` is
 *  already pinned to that maximum, so a collapse forces the browser to clamp
 *  the scroll position — and that clamp arrives as a scroll event with a
 *  band-sized upward delta, which reads as a deliberate scroll-up and reveals
 *  the band again. Reveal, clamp, collapse, repeat: the band visibly bounces.
 *  Freezing the state here breaks that loop at the source, and covers iOS
 *  rubber-band spring-back at the bottom for free (same region, same
 *  spurious upward deltas).
 *
 *  Must exceed the band's rendered height or the clamp escapes the zone. The
 *  band can't out-grow the 5rem `max-height` cap on `.hp-title-bar__clip`
 *  (global.scss) at any text size, so 80px is the safe floor — keep the two
 *  in step if that cap changes. */
const BOTTOM_ZONE = 80;

export const useCollapsingTitle = (
  contentRef: RefObject<HTMLIonContentElement | null>,
) => {
  const [collapsed, setCollapsed] = useState(false);
  const lastY = useRef(0);
  // The scroll element, needed for `scrollHeight`/`clientHeight` — the scroll
  // event's detail carries only `scrollTop`. Resolved once on mount because
  // `getScrollElement` is async and the handler has to stay synchronous.
  const scrollElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    let cancelled = false;
    void content.getScrollElement().then((el) => {
      if (!cancelled) scrollElRef.current = el;
    });
    return () => {
      cancelled = true;
    };
  }, [contentRef]);

  const onScroll = useCallback((e: CustomEvent<ScrollDetail>) => {
    const y = e.detail?.scrollTop ?? 0;
    const delta = y - lastY.current;

    // Under the deadzone: leave `lastY` alone so the movement keeps adding up
    // against the same anchor instead of being forgotten frame by frame.
    if (Math.abs(delta) < DIRECTION_DEADZONE) return;
    lastY.current = y;

    // Top zone always shows the band. This also covers iOS rubber-band
    // overscroll, where `scrollTop` goes negative and the delta on the way
    // back down would otherwise read as a genuine downward scroll.
    if (y <= TOP_ZONE) {
      setCollapsed(false);
      return;
    }

    // Bottom zone holds whatever state the band is already in. `lastY` is
    // still updated above, so leaving the zone compares against where the
    // user actually is rather than a stale anchor.
    const el = scrollElRef.current;
    if (el && y >= el.scrollHeight - el.clientHeight - BOTTOM_ZONE) return;

    setCollapsed(delta > 0);
  }, []);

  return { collapsed, onScroll };
};
